import { NSABoxSecret, NSARouterMakerResult } from "namirasoft-account-react";
import { NSBox, NSBoxBoolean, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { ProviderDelayUnit, ProviderLimitPerUnit, ProviderRow, ProviderType } from "namirasoft-telegram-sender";
import React, { createRef } from "react";
import { TelegramSenderEntities } from "../TelegramSenderEntities";
import { TelegramSenderLayout } from "../TelegramSenderLayout";
import { TelegramSenderRouterProps } from "../TelegramSenderRouterProps";

export interface ProviderEditPageProps extends NSARouterMakerResult, TelegramSenderRouterProps
{
	isEdit: boolean;
}

interface ProviderEditPageState
{
	type: ProviderType
}

export default class ProviderEditPage extends React.Component<ProviderEditPageProps, ProviderEditPageState> 
{
	private NSBoxString_Name = createRef<NSBoxString>();
	private NSBoxString_ChatID = createRef<NSBoxString>();
	private NSBoxString_MessageThreadID = createRef<NSBoxString>();
	private NSBoxEnum_Type = createRef<NSBoxEnum<ProviderType>>();
	private NSABoxSecret_APIToken = createRef<NSABoxSecret>();
	private NSBoxBoolean_Limit = createRef<NSBoxBoolean>();
	private NSBoxInteger_Limit_Count = createRef<NSBoxInteger>();
	private NSBoxInteger_Limit_Per_Value = createRef<NSBoxInteger>();
	private NSBoxEnum_Limit_Per_Unit = createRef<NSBoxEnum<ProviderLimitPerUnit>>();
	private NSBoxBoolean_Delay = createRef<NSBoxBoolean>();
	private NSBoxInteger_Delay_Value = createRef<NSBoxInteger>();
	private NSBoxEnum_Delay_Unit = createRef<NSBoxEnum<ProviderDelayUnit>>();
	private NSBoxTextArea_Description = createRef<NSBoxTextArea>();

	constructor(props: ProviderEditPageProps)
	{
		super(props);
		this.state = {
			type: ProviderType.NamirasoftBot
		};
	}
	override render()
	{
		let extra_tabs = [];
		extra_tabs.push(
			{
				title: "Limit",
				clicked: true,
				getContent: () =>
				{
					return (
						<NSColumn style={{ width: NSBox.width.double }}>
							<NSRow>
								<NSBoxBoolean
									ref={this.NSBoxBoolean_Limit}
									title="Limit"
									required
									style={{ width: "100%" }}
									defaultValue={false}
									onChanged={() => this.setState({})}
									info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-limit" }}
								/>
							</NSRow>
							<NSRow style={this.NSBoxBoolean_Limit.current?.getValueBool() ? {} : { display: "none" }}>
								<NSBoxInteger
									ref={this.NSBoxInteger_Limit_Count}
									title="Limit Count"
									required
									min={0}
									style={{ width: "100%" }}
									info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-limit-count" }}
								/>
							</NSRow>
							<NSRow style={this.NSBoxBoolean_Limit.current?.getValueBool() ? {} : { display: "none" }}>
								<NSBoxInteger
									ref={this.NSBoxInteger_Limit_Per_Value}
									title="Limit Per Value"
									required
									min={1}
									info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-limit-per-value" }}
								/>
								<NSBoxEnum<ProviderLimitPerUnit>
									ref={this.NSBoxEnum_Limit_Per_Unit}
									title="Limit Per Unit"
									required
									multiple={false}
									getEnumObject={() => ProviderLimitPerUnit}
									info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-limit-per-unit" }}
								/>
							</NSRow>
						</NSColumn>
					);
				}
			},
			{
				title: "Delay",
				clicked: true,
				getContent: () =>
				{
					return (
						<NSColumn style={{ width: NSBox.width.double }}>
							<NSRow>
								<NSBoxBoolean
									ref={this.NSBoxBoolean_Delay}
									title="Delay"
									required
									style={{ width: "100%" }}
									defaultValue={false}
									onChanged={() => this.setState({})}
									info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-delay" }}
								/>
							</NSRow>
							<NSRow style={this.NSBoxBoolean_Delay.current?.getValueBool() ? {} : { display: "none" }}>
								<NSBoxInteger
									ref={this.NSBoxInteger_Delay_Value}
									title="Delay Value"
									required
									min={1}
									info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-delay-value" }}
								/>
								<NSBoxEnum<ProviderDelayUnit>
									ref={this.NSBoxEnum_Delay_Unit}
									title="Delay Unit"
									required
									multiple={false}
									getEnumObject={() => ProviderDelayUnit}
									info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-delay-unit" }}
								/>
							</NSRow>
						</NSColumn>
					);
				}
			}
		);

		return (
			<TelegramSenderLayout<ProviderRow, ProviderRow>
				{...this.props}
				action={{
					description: this.props.isEdit ? "This section allows you to update the details of an existing Telegram provider." : "This section allows you to enter information to create a provider so you can send messages. Click the info icon for more information about each field."
				}}
				pages={{
					entity: TelegramSenderEntities.provider(this.props),
					edit: {
						...this.props,
						ui: {
							tab_page_edit: {
								editor: {
									getInputRow: () =>
									{
										let name = this.NSBoxString_Name.current?.getValue() ?? "";
										let type = this.NSBoxEnum_Type.current?.getValueEnumOne(ProviderType, ProviderType.NamirasoftBot) ?? ProviderType.NamirasoftBot;
										let meta = null;

										let chat_id = this.NSBoxString_ChatID.current?.getValue() ?? "";
										let message_thread_id = this.NSBoxString_MessageThreadID.current?.getValue() ?? ""

										if (type === ProviderType.NamirasoftBot)
										{
											meta = { chat_id, message_thread_id };
										}
										else if (type === ProviderType.CustomBot)
										{
											let api_token = this.NSABoxSecret_APIToken.current?.getValue() ?? "";
											meta = {
												chat_id, message_thread_id, api_token
											};
										}

										// Limit
										let limit: boolean = this.NSBoxBoolean_Limit.current?.getValueBool() ?? false;
										let limit_count: number | null = null;
										let limit_per_value: number | null = null;
										let limit_per_unit: ProviderLimitPerUnit | null = null;
										if (limit)
										{
											limit_count = this.NSBoxInteger_Limit_Count.current?.getValue() ?? 0;
											limit_per_value = this.NSBoxInteger_Limit_Per_Value.current?.getValue() ?? 0;
											limit_per_unit = this.NSBoxEnum_Limit_Per_Unit.current?.getValueEnumOne(ProviderLimitPerUnit, ProviderLimitPerUnit.Second) ?? ProviderLimitPerUnit.Second;
										}

										// Delay
										let delay: boolean = this.NSBoxBoolean_Delay.current?.getValueBool() ?? false;
										let delay_value: number | null = null;
										let delay_unit: ProviderDelayUnit | null = null;
										if (delay)
										{
											delay_value = this.NSBoxInteger_Delay_Value.current?.getValue() ?? 0;
											delay_unit = this.NSBoxEnum_Delay_Unit.current?.getValueEnumOne(ProviderDelayUnit, ProviderDelayUnit.Second) ?? ProviderDelayUnit.Second;
										}

										let description: string = this.NSBoxTextArea_Description.current?.getValue() ?? "";

										return {
											id: "",
											user_id: "",
											workspace_id: "",

											name,
											type,
											meta,
											e_verified: true,

											limit,
											limit_count,
											limit_per_value,
											limit_per_unit,

											delay,
											delay_value,
											delay_unit,

											description,

											created_at: new Date(),
											updated_at: new Date()
										};
									},
									setRow: (row) =>
									{
										this.NSBoxString_Name.current?.setValue(row.name);
										this.NSBoxEnum_Type.current?.setValue(row.type);
										this.NSBoxString_ChatID.current?.setValue(row.meta.chat_id);
										this.NSBoxString_MessageThreadID.current?.setValue(row.meta.message_thread_id);
										this.setState({ type: row.type }, () =>
										{
											if (row.type === ProviderType.NamirasoftBot)
											{
											}
											if (row.type === ProviderType.CustomBot)
											{
												this.NSABoxSecret_APIToken.current?.setValue(row.meta.api_token);
											}
										});
										this.NSBoxBoolean_Limit.current?.setValue(row.limit);
										if (row.limit)
										{
											this.NSBoxInteger_Limit_Count.current?.setValue(row.limit_count);
											this.NSBoxInteger_Limit_Per_Value.current?.setValue(row.limit_per_value);
											this.NSBoxEnum_Limit_Per_Unit.current?.setValue(row.limit_per_unit);
										}
										this.NSBoxBoolean_Delay.current?.setValue(row.delay);
										if (row.delay)
										{
											this.NSBoxInteger_Delay_Value.current?.setValue(row.delay_value);
											this.NSBoxEnum_Delay_Unit.current?.setValue(row.delay_unit);
										}
										this.NSBoxTextArea_Description.current?.setValue(row.description ?? "");
									}
								},
								ui: {
									extra_tabs
								}
							}
						}
					}
				}}
			>
				<NSColumn>
					<NSRow>
						<NSBoxString
							ref={this.NSBoxString_Name}
							title="Name"
							required
							info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-name" }}
						/>
						<NSBoxEnum<ProviderType>
							ref={this.NSBoxEnum_Type}
							title="Type"
							multiple={false}
							required
							getEnumObject={() => ProviderType}
							onChanged={() =>
							{
								let type = this.NSBoxEnum_Type.current?.getValueEnumOne(ProviderType, ProviderType.NamirasoftBot) ?? ProviderType.NamirasoftBot;
								this.setState({ type });
							}}
							searchable={false}
							info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-type" }}
						/>
					</NSRow>
					<NSRow>
						<NSBoxString
							ref={this.NSBoxString_ChatID}
							required
							title="Chat ID"
							info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-chat-id" }}
						/>
						<NSBoxString
							ref={this.NSBoxString_MessageThreadID}
							required={false}
							title="Message Thread ID"
							info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-message-thread-id" }}
						/>
					</NSRow>
					{
						this.state.type === ProviderType.CustomBot &&
						<>
							<NSRow>
								<NSABoxSecret
									ref={this.NSABoxSecret_APIToken}
									{...this.props}
									required
									title="API Token"
									info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-api-token" }}
								/>
							</NSRow>
						</>
					}
					<NSRow>
						<NSBoxTextArea
							ref={this.NSBoxTextArea_Description}
							title="Description"
							required={false}
							rows={5}
							style={{ width: "100%" }}
							info={{ link: "https://namirasoft.com/telegram-sender/console-guide/provider/#field-description" }}
						/>
					</NSRow>
				</NSColumn>
			</TelegramSenderLayout >
		);
	}
}